import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import LineLogin from './components/auth/LineLogin';
import Layout, { firstAllowedPath } from './components/Layout';
import ProductionLines from './components/production-lines/ProductionLines';
import Users from './components/users/Users';
import ImportProduction from './components/import/ImportProduction';
import AssignProduction from './components/assignments/AssignProduction';
import ManageAssignments from './components/assignments/ManageAssignments';
import MyProduction from './components/my-production/MyProduction';
import ProductionHistory from './components/my-production/ProductionHistory';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminAppearance from './components/admin/AdminAppearance';
import ProductionLineView from './components/admin/ProductionLineView';
import ProblemTypes from './components/admin/ProblemTypes';
import AdminSearch from './components/admin/AdminSearch';
import ShiftOutputReport from './components/admin/ShiftOutputReport';
import LineDashboard from './components/admin/LineDashboard';
import AdminCheckpoints from './components/admin/AdminCheckpoints';
import MaterialSpoolSpecs from './components/admin/MaterialSpoolSpecs';
import WinderCycles from './components/admin/WinderCycles';
import RoleAccess from './components/admin/RoleAccess';
import PackingStation from './components/packaging/PackingStation';
import LargeWarehouseView from './components/warehouse/LargeWarehouseView';
import SurplusView from './components/warehouse/SurplusView';
import DyeDemand from './components/dye/DyeDemand';
import Materials from './components/analytics/Materials';
import Overview from './components/analytics/Overview';
import Standards from './components/analytics/Standards';
import LineHistory from './components/analytics/LineHistory';
import Downtime from './components/analytics/Downtime';
import Production from './components/analytics/Production';
import Lines from './components/analytics/Lines';
import Shifts from './components/analytics/Shifts';
import Orders from './components/analytics/Orders';
import Capacity from './components/analytics/Capacity';
import Ranking from './components/analytics/Ranking';
import Definitions from './components/analytics/Definitions';

/** Start page of a role that has no tab ticked in "Dostęp do zakładek" yet. */
const NoTabs: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="card" style={{ padding: 24, color: '#4a5568' }}>
      {t('roleAccess.noTabs')}
    </div>
  );
};

const PackagingRedirect: React.FC = () => {
  const { isPackaging, isTracker, isCustomRole, tabs, can } = useAuth();
  if (isPackaging) return <Navigate to="/stacja-pakowania" replace />;
  // A role added by the admin has no production of its own — it starts on its first allowed tab.
  if (isCustomRole) {
    if (tabs === null) return null;
    const start = firstAllowedPath(can);
    return start ? <Navigate to={start} replace /> : <NoTabs />;
  }
  // Tracker nie ma wlasnej produkcji — jego strona startowa to lista linii do podgladu.
  if (isTracker) return <Navigate to="/production-lines" replace />;
  return <MyProduction />;
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  // Check if we're still loading auth state from localStorage
  const [isLoading, setIsLoading] = React.useState(true);
  
  React.useEffect(() => {
    // Give AuthContext time to load from localStorage
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Show nothing while loading to prevent flash of redirect
  if (isLoading) {
    return null;
  }
  
  // Redirect to /login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // If authenticated, redirect to home (prevent access to login/line-login pages)
  // BUT: Don't redirect immediately - let Login component handle shift selection first
  // We'll handle redirect in Login component after shift is selected
  return <>{children}</>;
};

/**
 * Route guard by managed tab (Admin -> Dostęp do zakładek). While the tab list is still
 * loading nothing is rendered, so a permitted page never flashes a redirect.
 */
const TabRoute: React.FC<{ tab: string; children: React.ReactNode }> = ({ tab, children }) => {
  const { isAuthenticated, tabs, can } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (tabs === null) return null;
  if (!can(tab)) return <Navigate to="/" />;
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/line-login" element={<PublicRoute><LineLogin /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
                <Route index element={<PackagingRedirect />} />
                <Route path="production-lines" element={<TabRoute tab="production_lines"><ProductionLines /></TabRoute>} />
                <Route path="zapotrzebowanie-barwnik" element={<TabRoute tab="dye_demand"><DyeDemand /></TabRoute>} />
                <Route path="production-history/:itemId" element={<ProductionHistory />} />
          <Route path="import" element={<TabRoute tab="import"><ImportProduction /></TabRoute>} />
          <Route path="assign" element={<TabRoute tab="assign"><AssignProduction /></TabRoute>} />
          <Route
            path="manage-assignments"
            element={
              <TabRoute tab="manage_assignments">
                <ManageAssignments />
              </TabRoute>
            }
          />
          <Route
            path="users"
            element={
              <TabRoute tab="users">
                <Users />
              </TabRoute>
            }
          />
          <Route
            path="admin"
            element={
              <TabRoute tab="admin_panel">
                <AdminDashboard />
              </TabRoute>
            }
          />
          <Route
            path="admin/wyglad"
            element={
              <TabRoute tab="appearance">
                <AdminAppearance />
              </TabRoute>
            }
          />
          <Route
            path="admin/production-lines/:id/view"
            element={
              <PrivateRoute>
                <ProductionLineView />
              </PrivateRoute>
            }
          />
          <Route
            path="admin/search"
            element={
              <TabRoute tab="search">
                <AdminSearch />
              </TabRoute>
            }
          />
          <Route
            path="admin/shift-output"
            element={
              <TabRoute tab="shift_output">
                <ShiftOutputReport />
              </TabRoute>
            }
          />
          <Route
            path="admin/dashboard-linii"
            element={
              <TabRoute tab="line_dashboard">
                <LineDashboard />
              </TabRoute>
            }
          />
          <Route
            path="admin/checkpointy"
            element={
              <TabRoute tab="checkpoints">
                <AdminCheckpoints />
              </TabRoute>
            }
          />
          <Route path="analityka/przeglad" element={<TabRoute tab="analytics_overview"><Overview /></TabRoute>} />
          <Route path="analityka/historia-linii" element={<TabRoute tab="analytics_line_history"><LineHistory /></TabRoute>} />
          <Route path="analityka/straty" element={<TabRoute tab="analytics_downtime"><Downtime /></TabRoute>} />
          <Route path="analityka/produkcja" element={<TabRoute tab="analytics_production"><Production /></TabRoute>} />
          <Route path="analityka/linie" element={<TabRoute tab="analytics_lines"><Lines /></TabRoute>} />
          <Route path="analityka/zmiany" element={<TabRoute tab="analytics_shifts"><Shifts /></TabRoute>} />
          <Route path="analityka/zlecenia" element={<TabRoute tab="analytics_orders"><Orders /></TabRoute>} />
          <Route path="analityka/capacity" element={<TabRoute tab="analytics_capacity"><Capacity /></TabRoute>} />
          <Route path="analityka/ranking" element={<TabRoute tab="analytics_ranking"><Ranking /></TabRoute>} />
          <Route path="analityka/definicje" element={<TabRoute tab="analytics_definitions"><Definitions /></TabRoute>} />
          <Route path="analityka/standardy" element={<TabRoute tab="analytics_standards"><Standards /></TabRoute>} />
          <Route path="analityka/materialy" element={<TabRoute tab="materials"><Materials /></TabRoute>} />
          <Route
            path="admin/parametry-szpul"
            element={
              <TabRoute tab="spool_specs">
                <MaterialSpoolSpecs />
              </TabRoute>
            }
          />
          <Route
            path="admin/nawijarka"
            element={
              <TabRoute tab="winder">
                <WinderCycles />
              </TabRoute>
            }
          />
          <Route
            path="admin/problem-types"
            element={
              <TabRoute tab="problem_types">
                <ProblemTypes />
              </TabRoute>
            }
          />
          <Route path="admin/dostep" element={<TabRoute tab="role_access"><RoleAccess /></TabRoute>} />
          <Route
            path="stacja-pakowania"
            element={
              <TabRoute tab="packaging_station">
                <PackingStation />
              </TabRoute>
            }
          />
          <Route
            path="admin/stacja-pakowania"
            element={
              <TabRoute tab="packaging_station">
                <PackingStation />
              </TabRoute>
            }
          />
          <Route path="magazyn-duzych-gabarytow" element={<TabRoute tab="large_warehouse"><LargeWarehouseView /></TabRoute>} />
          <Route
            path="nadwyzka-materialowa"
            element={
              <TabRoute tab="surplus">
                <SurplusView />
              </TabRoute>
            }
          />
        </Route>
        {/* Default route - redirect to login if not authenticated */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
